import { Separator, TitleDescription } from "components/custom"
import { DataTable, DateFormat, Icon, Number, Tag, Text } from "components/common"
import { useEffect, useState } from "react"
import { capitalizeFirstLetter, joinBytItems, localisedValue } from "utils"
import { useTranslation } from "react-i18next"
import theme from "utils/themeVars"
import { useGetClientProposalsQuery, useGetLeadProposalsQuery } from "features/shared/api"
import { skipToken } from "@reduxjs/toolkit/dist/query"

const ProposalTable = ({ lead, client, getCount = () => {} }) => {
  const { t } = useTranslation()
  const columns = [
    { title: t("Lead ID"), dataIndex: "lead" },
    { title: t("Unit Details"), dataIndex: "unit" },
    { title: t("Offer"), dataIndex: "offer" },
    { title: t("Proposal"), dataIndex: "pdf" },
    { title: t("Staff"), dataIndex: "staff" },
  ]

  const [filter, setFilter] = useState({
    page: 0,
    pageLimit: 10,
  })

  const onChangePage = (page, pageSize) => {
    setFilter(prev => ({ ...prev, pageLimit: pageSize, page: page }))
  }

  const {
    data: proposals,
    isFetching,
    error,
    refetch,
  } = useGetClientProposalsQuery(lead ? skipToken : { id: client?.id, params: filter })

  const {
    data: leadProposals,
    isFetching: isFetchingLead,
    error: errorLead,
    refetch: refetchLead,
  } = useGetLeadProposalsQuery(lead ? { id: lead?.id, params: filter } : skipToken)

  useEffect(() => {
    if (proposals?.count) {
      getCount(proposals.count)
    }
    if (leadProposals?.count) {
      getCount(leadProposals.count)
    }
  }, [proposals?.count, leadProposals?.count])

  const getColumnData = (e, index) => {
    return {
      key: index,
      lead: e?.inquiryId,
      unit: (
        <TitleDescription
          title={
            <>
              {e?.unitNumber}
              <Separator />
              {localisedValue({ key: "projectName", object: e })}
            </>
          }
          description={localisedValue({ key: "unitType", object: e })}
        />
      ),
      offer: (
        <TitleDescription
          title={
            <>
              {t("Price")}:&nbsp;
              <Number tooltip type="secondaryLight" value={e?.dealPrice} isCurrency />
              &nbsp;
              {e?.discountPercentage > 0 ? (
                <Number type="danger" value={e?.discountPercentage} wrapValue={v => `(-${v}%)`} />
              ) : null}
            </>
          }
          gapSpace={4}
          description={joinBytItems(
            [
              <Number value={e?.instalmentDuration} wrapValue={v => `${v} Months`} />,
              <Text>
                {`${
                  e?.instalmentFrequency
                    ? capitalizeFirstLetter(localisedValue({ key: "instalmentFrequency", object: e })) + " Payment"
                    : "-"
                } `}
              </Text>,
            ],
            <Separator />
          )}
        />
      ),
      pdf: (
        <a key={index} href={e?.attachment} target="_blank" rel="noreferrer">
          <Icon style={{ color: theme["primary-color"] }} key={index} icon="BsFileEarmarkText" size="1em" />{" "}

          
          {t("Attachment")}
          {e?.attachmentId}
        </a>
      ),
      staff: (
        <TitleDescription
          title={
            <>
              {localisedValue({ key: "createdBy", object: e })}&nbsp;
              <Tag
                type={!(e?.teamType === "Telesales") && "light"}
                color={e?.teamType === "Telesales" && "#FFC000"}
                children={e?.teamType}
              />
            </>
          }
          description={<DateFormat date={e?.createdAt} />}
        />
      ),
    }
  }

  const tableData = lead
    ? leadProposals?.data?.map((e, index) => {
        return getColumnData(e, index)
      })
    : proposals?.data?.map((e, index) => {
        return getColumnData(e, index)
      })

  return (
    <DataTable
      tableLayout="auto"
      pagination={{ totalCount: lead ? leadProposals?.count : proposals?.count, pageOptions: [10, 25, 50] }}
      paginationOnBottom={true}
      paginationOnTop={false}
      columns={columns}
      data={tableData || []}
      loading={lead ? isFetchingLead : isFetching}
      noUrlPush={true}
      error={lead ? errorLead : error}
      onErrorRetry={lead ? refetchLead : refetch}
      stickyOffset={0}
      onChangePage={onChangePage}
    />
  )
}

export default ProposalTable
